import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  FiZap, FiTarget, FiGrid, FiLayers, FiTrendingUp, FiCoffee, FiWind,
  FiStar, FiShield, FiActivity, FiDroplet, FiSearch, FiRefreshCw,
  FiBox, FiAward, FiCpu, FiFeather, FiBarChart2, FiCircle
} from 'react-icons/fi';
import { supabase } from '../supabase';
import { dedupeRequest, getCacheKey } from '../utils/queryDeduplication';
import JellyfishLoader from './JellyfishLoader';
import './SidebarFilters.css';

const CATEGORY_ICONS = {
  'Lifestyle & Recovery': FiRefreshCw,
  'Diet & Nutrition': FiCoffee,
  'Supplement': FiCircle,
  'Vitamin & Mineral': FiStar,
  'Herbal & Natural Extracts': FiFeather,
  'Pharmaceutical': FiBox,
  'Medical Technology & Devices': FiCpu,
  'Topical & Enzyme Treatments': FiDroplet,
  'Detox & Absorption': FiWind,
};

const BIOMARKER_CATEGORY_ICONS = {
  'Metabolic': FiActivity,
  'Lipid': FiBarChart2,
  'Liver': FiLayers,
  'Kidney': FiTarget,
  'Electrolytes': FiZap,
  'Inflammation': FiShield,
  'Nutrients': FiAward,
  'Hormones': FiTrendingUp,
  'Hematology': FiGrid,
};

// Simple debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setDebouncedValue(value), delay);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [value, delay]);

  return debouncedValue;
};

const SidebarFilters = ({ 
  selectedHacks, setSelectedHacks, 
  selectedCategories, setSelectedCategories,
  selectedBiomarkers, setSelectedBiomarkers,
  selectedBiomarkerCategories, setSelectedBiomarkerCategories
}) => {
  const [biohacks, setBiohacks] = useState([]);
  const [biomarkers, setBiomarkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState({ biohacks: {}, biomarkers: {} });
  const [search, setSearch] = useState({ biohacks: '', biomarkers: '' });
  const [showSearch, setShowSearch] = useState({ biohacks: false, biomarkers: false });

  const debouncedSearch = {
    biohacks: useDebounce(search.biohacks, 150),
    biomarkers: useDebounce(search.biomarkers, 150)
  };

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        // Use deduplication to prevent duplicate requests (especially from React Strict Mode)
        const biohacksCacheKey = getCacheKey('fetchSidebarBiohacks', 'all');
        const biomarkersCacheKey = getCacheKey('fetchSidebarBiomarkers', 'all');
        
        const [biohacksRes, biomarkersRes] = await Promise.all([
          dedupeRequest(biohacksCacheKey, async () => {
            const { data, error } = await supabase
              .from('biohacks')
              .select('id, name, category')
              .order('name')
              .limit(100);
            if (error) throw error;
            return { data, error };
          }),
          dedupeRequest(biomarkersCacheKey, async () => {
            const { data, error } = await supabase
              .from('biomarkers')
              .select('id, name, category')
              .order('name')
              .limit(100);
            if (error) throw error;
            return { data, error };
          })
        ]);

        if (biohacksRes.error) throw biohacksRes.error;
        if (biomarkersRes.error) throw biomarkersRes.error;

        setBiohacks(biohacksRes.data || []);
        setBiomarkers(biomarkersRes.data || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Organize items by category
  const itemsByCategory = useMemo(() => {
    const biohacksByCat = {};
    const biomarkersByCat = {};

    biohacks.forEach(hack => {
      if (hack.category) {
        if (!biohacksByCat[hack.category]) biohacksByCat[hack.category] = [];
        biohacksByCat[hack.category].push(hack);
      }
    });

    biomarkers.forEach(biomarker => {
      if (biomarker.category) {
        if (!biomarkersByCat[biomarker.category]) biomarkersByCat[biomarker.category] = [];
        biomarkersByCat[biomarker.category].push(biomarker);
      }
    });

    return { biohacks: biohacksByCat, biomarkers: biomarkersByCat };
  }, [biohacks, biomarkers]);

  // Get unique categories
  const categories = useMemo(() => ({
    biohacks: Object.keys(itemsByCategory.biohacks),
    biomarkers: Object.keys(itemsByCategory.biomarkers)
  }), [itemsByCategory]);

  // Filter categories and items based on search
  const filteredData = useMemo(() => {
    const filterItems = (items, searchTerm) => {
      if (!searchTerm) return items;
      const lower = searchTerm.toLowerCase();
      return items.filter(item => item.name.toLowerCase().includes(lower));
    };

    const filterCategories = (cats, itemsByCat, searchTerm) => {
      if (!searchTerm) return cats;
      const lower = searchTerm.toLowerCase();
      return cats.filter(cat => 
        (itemsByCat[cat] || []).some(item => item.name.toLowerCase().includes(lower))
      );
    };

    return {
      biohacks: {
        categories: filterCategories(categories.biohacks, itemsByCategory.biohacks, debouncedSearch.biohacks),
        itemsByCategory: Object.fromEntries(
          Object.entries(itemsByCategory.biohacks).map(([cat, items]) => [
            cat,
            filterItems(items, debouncedSearch.biohacks)
          ])
        )
      },
      biomarkers: {
        categories: filterCategories(categories.biomarkers, itemsByCategory.biomarkers, debouncedSearch.biomarkers),
        itemsByCategory: Object.fromEntries(
          Object.entries(itemsByCategory.biomarkers).map(([cat, items]) => [
            cat,
            filterItems(items, debouncedSearch.biomarkers)
          ])
        )
      }
    };
  }, [categories, itemsByCategory, debouncedSearch]);

  // Auto-expand categories when searching
  useEffect(() => {
    if (debouncedSearch.biohacks) {
      setExpanded(prev => ({
        ...prev,
        biohacks: Object.fromEntries(filteredData.biohacks.categories.map(cat => [cat, true]))
      }));
    } else {
      setExpanded(prev => ({ ...prev, biohacks: {} }));
    }
  }, [debouncedSearch.biohacks, filteredData.biohacks.categories]);

  useEffect(() => {
    if (debouncedSearch.biomarkers) {
      setExpanded(prev => ({
        ...prev,
        biomarkers: Object.fromEntries(filteredData.biomarkers.categories.map(cat => [cat, true]))
      }));
    } else {
      setExpanded(prev => ({ ...prev, biomarkers: {} }));
    }
  }, [debouncedSearch.biomarkers, filteredData.biomarkers.categories]);

  // Toggle functions
  const toggleCategory = useCallback((category, type) => {
    const items = itemsByCategory[type][category] || [];
    const itemIds = items.map(item => item.id);
    const itemIdsSet = new Set(itemIds);
    
    const isSelected = type === 'biohacks' 
      ? selectedCategories.includes(category)
      : selectedBiomarkerCategories.includes(category);

    if (isSelected) {
      if (type === 'biohacks') {
        setSelectedCategories(prev => prev.filter(c => c !== category));
        setSelectedHacks(prev => prev.filter(id => !itemIdsSet.has(id)));
      } else {
        setSelectedBiomarkerCategories(prev => prev.filter(c => c !== category));
        setSelectedBiomarkers(prev => prev.filter(id => !itemIdsSet.has(id)));
      }
    } else {
      if (type === 'biohacks') {
        setSelectedCategories(prev => [...prev, category]);
        setSelectedHacks(prev => {
          const existing = new Set(prev);
          return [...prev, ...itemIds.filter(id => !existing.has(id))];
        });
      } else {
        setSelectedBiomarkerCategories(prev => [...prev, category]);
        setSelectedBiomarkers(prev => {
          const existing = new Set(prev);
          return [...prev, ...itemIds.filter(id => !existing.has(id))];
        });
      }
    }
  }, [itemsByCategory, selectedCategories, selectedBiomarkerCategories, selectedHacks, selectedBiomarkers]);

  const toggleItem = useCallback((itemId, type) => {
    const allItems = type === 'biohacks' ? biohacks : biomarkers;
    const item = allItems.find(i => i.id === itemId);
    if (!item) return;

    const isSelected = type === 'biohacks'
      ? selectedHacks.includes(itemId)
      : selectedBiomarkers.includes(itemId);

    if (isSelected) {
      if (type === 'biohacks') {
        setSelectedHacks(prev => prev.filter(id => id !== itemId));
        // Remove category if no items from that category remain selected
        if (item.category) {
          const categoryItems = itemsByCategory.biohacks[item.category] || [];
          const remaining = selectedHacks.filter(id => 
            id !== itemId && categoryItems.some(ci => ci.id === id)
          );
          if (remaining.length === 0) {
            setSelectedCategories(prev => prev.filter(c => c !== item.category));
          }
        }
      } else {
        setSelectedBiomarkers(prev => prev.filter(id => id !== itemId));
        if (item.category) {
          const categoryItems = itemsByCategory.biomarkers[item.category] || [];
          const remaining = selectedBiomarkers.filter(id => 
            id !== itemId && categoryItems.some(ci => ci.id === id)
          );
          if (remaining.length === 0) {
            setSelectedBiomarkerCategories(prev => prev.filter(c => c !== item.category));
          }
        }
      }
    } else {
      if (type === 'biohacks') {
        setSelectedHacks(prev => [...prev, itemId]);
      } else {
        setSelectedBiomarkers(prev => [...prev, itemId]);
      }
    }
  }, [biohacks, biomarkers, itemsByCategory, selectedHacks, selectedBiomarkers, selectedCategories, selectedBiomarkerCategories]);

  const toggleExpansion = useCallback((category, type) => {
    setExpanded(prev => ({
      ...prev,
      [type]: { ...prev[type], [category]: !prev[type][category] }
    }));
  }, []);

  // Render category section
  const renderSection = useCallback((type) => {
    const isBiohack = type === 'biohacks';
    const title = isBiohack ? 'Biohacks' : 'Biomarkers';
    const iconMap = isBiohack ? CATEGORY_ICONS : BIOMARKER_CATEGORY_ICONS;
    const data = filteredData[type];
    const selectedCats = isBiohack ? selectedCategories : selectedBiomarkerCategories;
    const selectedItems = isBiohack ? selectedHacks : selectedBiomarkers;
    const searchTerm = search[type];
    const isSearchVisible = showSearch[type];
    const expandedCats = expanded[type];

    const selectedCatsSet = new Set(selectedCats);
    const selectedItemsSet = new Set(selectedItems);

    return (
      <div className={isBiohack ? 'biohack-section' : 'biomarker-section'}>
        <div className="section-header">
          <h3 className={`section-title ${isSearchVisible ? 'hidden' : ''}`}>{title}</h3>
          <div className="section-search-container">
            <input
              type="text"
              className={`section-search-input ${isSearchVisible ? 'expanded' : ''}`}
              placeholder="Search categories..."
              value={searchTerm}
              onChange={(e) => setSearch(prev => ({ ...prev, [type]: e.target.value }))}
              autoFocus={isSearchVisible}
            />
            <button
              className={`section-search-btn ${isSearchVisible ? 'active' : ''}`}
              onClick={() => {
                setShowSearch(prev => ({ ...prev, [type]: !prev[type] }));
                if (isSearchVisible) setSearch(prev => ({ ...prev, [type]: '' }));
              }}
              title={isSearchVisible ? 'Close search' : 'Search categories'}
            >
              <FiSearch />
            </button>
          </div>
        </div>
        <div className="categories-list">
          {data.categories.map((category) => {
            const items = data.itemsByCategory[category] || [];
            const isExpanded = expandedCats[category] || (debouncedSearch[type] && items.length > 0);
            const isSelected = selectedCatsSet.has(category);
            const IconComponent = iconMap[category] || (isBiohack ? FiZap : FiTarget);

            return (
              <div key={category} className="category-item">
                <div 
                  className={`category-header ${isSelected ? 'selected' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (items.length > 0) {
                      toggleExpansion(category, type);
                    } else {
                      toggleCategory(category, type);
                    }
                  }}
                >
                  <div className="category-icon-container">
                    <IconComponent className="category-icon" />
                  </div>
                  <span className="category-name">{category}</span>
                  <span className="category-count">{items.length}</span>
                  {items.length > 0 && (
                    <button 
                      className="category-select-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCategory(category, type);
                      }}
                      title={isSelected ? 'Deselect all' : 'Select all'}
                    >
                      {isSelected ? '✓' : '+'}
                    </button>
                  )}
                </div>

                {items.length > 0 && (
                  <div className={`biohacks-nested ${isExpanded ? 'expanded' : ''}`}>
                    <div className="nested-vertical-line"></div>
                    <div className="nested-content">
                      {items.slice(0, isExpanded ? items.length : 3).map((item) => (
                        <div 
                          key={item.id} 
                          className={`nested-item ${selectedItemsSet.has(item.id) ? 'selected' : ''}`}
                          onClick={() => toggleItem(item.id, type)}
                        >
                          <span className="nested-item-text">{item.name}</span>
                        </div>
                      ))}
                      {items.length > 3 && !isExpanded && (
                        <div 
                          className="nested-item show-more"
                          onClick={() => toggleExpansion(category, type)}
                        >
                          <span className="nested-item-text">+{items.length - 3} more</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }, [filteredData, selectedCategories, selectedBiomarkerCategories, selectedHacks, selectedBiomarkers, search, showSearch, expanded, debouncedSearch, toggleCategory, toggleItem, toggleExpansion]);

  return (
    <nav className="sidebar-filters">
      <div className={`filter-content ${loading ? 'loading' : ''}`}>
        {loading ? (
          <div className="sidebar-loader-wrapper">
            <JellyfishLoader />
          </div>
        ) : (
          <>
            {error ? (
              <div className="error-message">{error}</div>
            ) : (
              <>
                {categories.biohacks.length > 0 && renderSection('biohacks')}
                {categories.biomarkers.length > 0 && renderSection('biomarkers')}
              </>
            )}
          </>
        )}
      </div>
    </nav>
  );
};

export default React.memo(SidebarFilters);